import { useState } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Alert from '@cloudscape-design/components/alert'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import FormField from '@cloudscape-design/components/form-field'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Select from '@cloudscape-design/components/select'
import type { AutonomousResult, BuyerJourneyStage, ContentType } from '@/lib/types'
import styles from './ContentCard.module.css'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ContentCardProps {
  result: AutonomousResult
  onQueue: () => void
  queueStatus: 'idle' | 'pushing' | 'queued' | 'error'
  queueError?: string
  buyerJourneyStage?: BuyerJourneyStage
  onStageChange?: (stage: BuyerJourneyStage) => void
}

// ─── Content type labels ──────────────────────────────────────────────────────

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  facebook_post: 'Facebook Post',
  twitter_post: 'Twitter / X',
  instagram_post: 'Instagram Post',
  linkedin_post: 'LinkedIn Post',
  blog_seo: 'Blog / SEO',
  email_outreach: 'Email Outreach',
  facebook_ad: 'Facebook Ad',
  instagram_ad: 'Instagram Ad',
  google_ads: 'Google Ads',
  gbp_post: 'Google Business',
}

// ─── Platform icons (18×18) ───────────────────────────────────────────────────

const CC_IG_GRAD_ID = 'cc-ig-grad'
const CC_IGA_GRAD_ID = 'cc-iga-grad'

function getPlatformIcon(type: ContentType) {
  switch (type) {
    case 'facebook_post':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#1877F2" />
          <path d="M16 8h-2.5C12.67 8 12 8.67 12 9.5V11h4l-.5 4h-3.5v8H8v-8H5v-4h3V9.5C8 6.46 10.46 4 13.5 4H16v4z" fill="#fff" />
        </svg>
      )
    case 'twitter_post':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#000" />
          <path d="M17.5 5h-2.3L12 9.4 9.8 5H5l5.4 7.4L5 19h2.3L12 14.1l2.7 4.9H20l-5.7-7.8L17.5 5z" fill="#fff" />
        </svg>
      )
    case 'instagram_post':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <defs>
            <linearGradient id={CC_IG_GRAD_ID} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F58529" />
              <stop offset="50%" stopColor="#DD2A7B" />
              <stop offset="100%" stopColor="#515BD4" />
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill={`url(#${CC_IG_GRAD_ID})`} />
          <rect x="6" y="6" width="12" height="12" rx="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="16.5" cy="7.5" r="1" fill="#fff" />
        </svg>
      )
    case 'linkedin_post':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#0A66C2" />
          <path d="M7 9h2v9H7V9zm1-1a1.25 1.25 0 110-2.5A1.25 1.25 0 018 8zm4 1h2v1.2c.4-.7 1.3-1.4 2.5-1.4C18.4 8.8 19 10.1 19 12v6h-2v-5.5c0-1-.3-1.7-1.2-1.7-.9 0-1.4.6-1.6 1.2-.1.2-.2.5-.2.8V18h-2V9z" fill="#fff" />
        </svg>
      )
    case 'email_outreach':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#EA4335" />
          <path d="M5 8l7 5 7-5V7H5v1zm0 1.5V17h14V9.5l-7 5-7-5z" fill="#fff" />
        </svg>
      )
    case 'blog_seo':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#4285F4" />
          <rect x="6" y="7" width="8" height="2" rx="1" fill="#fff" />
          <rect x="6" y="11" width="12" height="2" rx="1" fill="#fff" />
          <rect x="6" y="15" width="6" height="2" rx="1" fill="#fff" />
          <path d="M16 14l1.5 1.5L20 13" stroke="#34A853" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'facebook_ad':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#1877F2" opacity="0.6" />
          <path d="M16 8h-2.5C12.67 8 12 8.67 12 9.5V11h4l-.5 4h-3.5v8H8v-8H5v-4h3V9.5C8 6.46 10.46 4 13.5 4H16v4z" fill="#fff" />
          <circle cx="18" cy="18" r="5" fill="#F5C518" />
          <path d="M16.5 18l1 1 2-2" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'instagram_ad':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <defs>
            <linearGradient id={CC_IGA_GRAD_ID} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F58529" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#DD2A7B" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#515BD4" stopOpacity="0.6" />
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill={`url(#${CC_IGA_GRAD_ID})`} />
          <rect x="6" y="6" width="12" height="12" rx="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="16.5" cy="7.5" r="1" fill="#fff" />
          <circle cx="18" cy="18" r="5" fill="#F5C518" />
          <path d="M16.5 18l1 1 2-2" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'google_ads':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#202124" />
          <circle cx="8" cy="12" r="3" fill="#FBBC04" />
          <circle cx="14" cy="12" r="3" fill="#34A853" />
          <circle cx="18" cy="12" r="3" fill="#4285F4" />
        </svg>
      )
    case 'gbp_post':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#202124" />
          <path d="M12 5a5 5 0 00-5 5c0 3.5 5 9 5 9s5-5.5 5-9a5 5 0 00-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z" fill="#EA4335" />
        </svg>
      )
  }
}

// ─── Stage options ────────────────────────────────────────────────────────────

const STAGE_OPTIONS = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'consideration', label: 'Consideration' },
  { value: 'decision', label: 'Decision' },
]

// ─── Component ────────────────────────────────────────────────────────────────

export default function ContentCard({ result, onQueue, queueStatus, queueError, buyerJourneyStage, onStageChange }: ContentCardProps) {
  const [copied, setCopied] = useState(false)

  const { content_type, content, compliance, seo_metadata } = result
  const label = CONTENT_TYPE_LABELS[content_type]
  const issueCount = compliance.issues.length
  const isCompliant = compliance.approved

  function handleCopy() {
    void navigator.clipboard.writeText(content)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {
        // clipboard write failed silently — no user feedback needed
      })
  }

  // ─── Queue button ───────────────────────────────────────────────────────────

  function renderQueueButton() {
    if (queueStatus === 'queued') {
      return (
        <StatusIndicator type="success">In Queue</StatusIndicator>
      )
    }
    if (queueStatus === 'pushing') {
      return (
        <Button loading loadingText="Adding…" onClick={undefined}>
          + Queue
        </Button>
      )
    }
    if (queueStatus === 'error') {
      return (
        <FormField errorText={queueError}>
          <Button variant="normal" onClick={onQueue}>
            Retry
          </Button>
        </FormField>
      )
    }
    // idle
    return (
      <Button variant="primary" onClick={onQueue}>
        + Queue
      </Button>
    )
  }

  // ─── Header ─────────────────────────────────────────────────────────────────

  const headerLeft = (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      {/* icon + label + compliance badge */}
      {getPlatformIcon(content_type)}
      <Box variant="span" fontWeight="bold">{label}</Box>
      {isCompliant ? (
        <StatusIndicator type="success">Compliant</StatusIndicator>
      ) : (
        <StatusIndicator type="warning">
          {issueCount} issue{issueCount !== 1 ? 's' : ''}
        </StatusIndicator>
      )}
    </SpaceBetween>
  )

  const actions = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="normal" onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy'}
      </Button>
      {renderQueueButton()}
    </SpaceBetween>
  )

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={actions}
        >
          {headerLeft}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Content body */}
        <Box>
          <Box className={styles.contentText}>{content}</Box>
        </Box>

        {/* Buyer journey stage selector */}
        {onStageChange && (
          <FormField label="Buyer Journey Stage">
            <Select
              selectedOption={STAGE_OPTIONS.find(o => o.value === buyerJourneyStage) ?? STAGE_OPTIONS[0]}
              options={STAGE_OPTIONS}
              onChange={({ detail }) => onStageChange(detail.selectedOption.value as BuyerJourneyStage)}
            />
          </FormField>
        )}

        {/* SEO metadata — blog_seo only */}
        {content_type === 'blog_seo' && seo_metadata && (
          <Box>
            <SpaceBetween size="s">
              <Box variant="awsui-key-label">SEO Metadata</Box>
              <ColumnLayout columns={1} variant="text-grid">
                <SpaceBetween size="xxs">
                  <Box color="text-label" variant="small">Meta Title</Box>
                  <Box>{seo_metadata.meta_title}</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box color="text-label" variant="small">Meta Description</Box>
                  <Box>{seo_metadata.meta_description}</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box color="text-label" variant="small">Slug</Box>
                  <Box>{seo_metadata.slug}</Box>
                </SpaceBetween>
              </ColumnLayout>
            </SpaceBetween>
          </Box>
        )}

        {/* Compliance warning */}
        {!isCompliant && (
          <Alert type="warning" header="Compliance Issues">
            <SpaceBetween size="xs">
              <Box>
                <SpaceBetween size="xxs">
                  {compliance.issues.map((issue) => (
                    <Box key={issue} variant="p">• {issue}</Box>
                  ))}
                </SpaceBetween>
              </Box>
              {compliance.suggested_fixes.length > 0 && (
                <Box>
                  <Box variant="small" color="text-body-secondary">
                    Suggested fixes: {compliance.suggested_fixes.join('; ')}
                  </Box>
                </Box>
              )}
            </SpaceBetween>
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  )
}
